import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks
} from 'body-scroll-lock'
import {
  getTransitionDuration,
  getBreakpoints,
  viewportSize
} from './utils.js'
import A11yDialog from 'a11y-dialog'


let enableConsoleLogs = false
let logger = (...msg) => enableConsoleLogs && console.log('%cNavMenu:', 'color:yellow;', ...msg)


// The nav menu is essentially a modal dialog, because when it’s open,
// it covers the available screen space and should therefore trap tabs,
// etc., as explained here:
// https://plousia.com/blog/how-create-accessible-mobile-menu
// So we’re treating it with the same considerations.
//
// Because this menu is for small viewports only, however, it should also:
// - Be available when viewportSize().isSm.matches is true
// - If shown, hide when viewportSize().isSm.matches is false


export default class NavMenu {
  #staticButtonContents = `<svg class="icon u-mr-xxs" height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m4.5 6.5h12"/><path d="m4.498 10.5h11.997"/><path d="m4.5 14.5h7.995"/></g></svg><span class="u-vh">Open </span>Menu`
  #activeButtonContents = `<svg class="icon u-mr-xxs" height="21" viewBox="0 0 21 21" width="21" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" transform="translate(5 5)" aria-hidden="true" focusable="false"><path d="m10.5 10.5-10-10z"/><path d="m10.5.5-10 10"/></g></svg><span class="u-vh">Close </span>Menu`

  constructor() {
    this.siteHeader = document.getElementById('site-header')
    this.headerNav = document.getElementById('header-nav')
    try {
      this.navBreakpoints = getBreakpoints(this.headerNav)
    } catch (error) {
      console.error(error, this.headerNav)
    }
    this.detachedNodes = []

    this.handleBreakpointCallback = this.handleBreakpoint.bind(this)
    this.handleButtonToggleCallback = this.handleButtonToggle.bind(this)
    this.handleAnchorLinkCallback = this.handleAnchorLink.bind(this)
    this.handleResizeCallback = this.handleResize.bind(this)

    // this.setupEvent = new CustomEvent('navmenu', {
    //   detail: { isSetup: true }
    // })
    // this.destroyedEvent = new CustomEvent('navmenu', {
    //   detail: { isSetup: false }
    // })

    if (viewportSize(this.navBreakpoints.sm, this.navBreakpoints.lg).isSm.matches) {
      this.setupMenu()
    }

    this.setMenuTopOffset()

    // Watch for viewport changes
    viewportSize(this.navBreakpoints.sm, this.navBreakpoints.lg).isSm.addListener(this.handleBreakpointCallback)
  }

  setupMenu() {
    // Dispatch setup event
    logger('header nav: setting up')
    // this.headerNav.dispatchEvent(this.setupEvent)

    // Create menu
    this.navMenu = document.createElement('div')
    this.navMenu.id = 'menu-sm'
    this.navMenu.classList.add('nav-menu')
    this.navMenu.setAttribute('hidden', 'true')

    this.navMenuInner = document.createElement('div')
    this.navMenuInner.classList.add('nav-menu-inner')
    this.navMenu.append(this.navMenuInner)

    // Create menu toggle button
    this.navMenuBtn = document.createElement('button')
    this.navMenuBtn.classList.add('btn', 'menu-sm-toggle')
    this.navMenuBtn.setAttribute('aria-controls', 'menu-sm')
    this.navMenuBtn.setAttribute('aria-expanded', 'false')
    this.navMenuBtn.innerHTML = this.#staticButtonContents

    // Copy nav elements and put them in the menu
    let navCollection = this.getNavContent(true)
    this.navMenuInner.append(navCollection)

    // Add to the site header
    this.headerNav.append(this.navMenuBtn)
    this.headerNav.append(this.navMenu)

    // Set up dialog with header nav as focus container
    this.menuDialog = new A11yDialog(this.navMenu, this.headerNav)
    this.animationDuration = getTransitionDuration(this.navMenuInner)

    // Now a11y-dialog can take care of hiding/showing
    this.navMenu.hidden = false

    // Stick on an attribute for style scoping
    this.headerNav.setAttribute('data-menu-sm', 'true')

    // Handle button content regardless of how the menu dialog opens or closes
    this.menuDialog.on('show', () => this.handleButtonContent(true))
    this.menuDialog.on('hide', () => {
      // If the menu gets dismissed with the escape key, call this class' close()
      // method first to handle everything properly. That will trigger the hide
      // event again, and then handleButtonContent() gets called.
      if (event.detail?.key === 'Escape' && this.menuDialog.shown) {
        event.preventDefault()
        this.close()
        return
      }

      this.handleButtonContent(false)
    })

    this.navMenuBtn.addEventListener('click', this.handleButtonToggleCallback)
    document.addEventListener('click', this.handleAnchorLinkCallback)
    window.addEventListener('resize', this.handleResizeCallback)

    // this.open()
  }

  destroyMenu() {
    // Keep an array of the attributes that a11y-dialog adds to the dialog element,
    // so that we can easily clean them up when we need to destroy the dialog
    let a11yDialogAttrs = [
      'aria-hidden',
      'aria-modal',
      'role',
      'tabindex'
    ]

    // Tear it all down, including the aforementioned a11y-dialog attributes

    // a11y-dialog's destroy method removes its own event listeners
    this.menuDialog.destroy()
    // Clean up whatever's left
    a11yDialogAttrs.map(attr => this.navMenu.removeAttribute(attr))

    clearAllBodyScrollLocks()

    this.navMenu.classList.remove('is-closing')
    this.headerNav.setAttribute('data-menu-sm', 'false')

    // Clean up event listeners
    this.navMenuBtn.removeEventListener('click', this.handleButtonToggleCallback)
    document.removeEventListener('click', this.handleAnchorLinkCallback)
    window.removeEventListener('resize', this.handleResizeCallback)

    // Copy some nav elements and put them back in the site's header
    let navCollection = this.getNavContent(false)
    this.headerNav.append(navCollection)

    this.navMenuBtn.remove()
    // Annoyingly, this.navMenu.remove() doesn't work, but the following does
    document.getElementById('menu-sm').remove()

    // Dispatch destroyed event
    logger('header nav: done tearing down')
    // this.headerNav.dispatchEvent(this.destroyedEvent)
  }

  open(event) {
    // First, scroll to the top of the page to ensure the menu's toggle button
    // is fully within view before disabling body scrolling
    window.scrollTo(0, 0)
    disableBodyScroll(this.siteHeader)

    this.setMenuTopOffset()
    this.menuDialog.show()

    // Make sure this class is removed
    this.navMenu.classList.remove('is-closing')

    // Animate it
    this.navMenu.classList.add('is-open')
  }

  close(event) {
    enableBodyScroll(this.siteHeader)

    // Animate the menu closing
    this.navMenu.classList.add('is-closing')
    this.navMenu.classList.remove('is-open')

    // Update button content immediately
    this.handleButtonContent(false)

    // Give animation time to finish before hiding dialog
    setTimeout(() => {
      this.navMenu.classList.remove('is-closing')
      this.menuDialog.hide()
    }, this.animationDuration)
  }

  getNavContent(isSm) {
    // Grab each nav element that should get included in the menu,
    // and turn it into a fragment that can be operated on before
    // getting appended to the menu.
    //
    // Attributes for nav menu elements/nodes:
    //
    // [data-menu-attach] keeps track of the order these nodes should
    // be included by in the nav menu. Every element/section that should
    // get added to the nav menu dialog must have this attribute and
    // its value must be a number.
    //
    // [data-menu-detach] indicates an element should be removed
    // entirely when the nav menu is set up. Add [data-header-attach]
    // to the same element to ensure it gets reattached where it
    // belongs if the nav menu is destroyed.
    //
    // [data-header-attach] keeps track of the order by which some
    // of these nodes should be attached to the site's header when
    // the nav menu is destroyed. Its value must be a number.
    //
    // [data-menu-copy="true"] will only make a copy of that node,
    // and since its original will still exist, it won't get
    // attached anywhere when the nav menu is destroyed.

    let navNodes = [
      ...document.querySelectorAll('[data-menu-attach]'),
      ...document.querySelectorAll('[data-menu-detach]'),
      ...this.detachedNodes
    ]
    let navCollection = document.createDocumentFragment()

    logger('nav nodes:', navNodes)

    // When the nav menu gets created, sort every element, but when
    // it gets destroyed, only bother sorting the elements that will
    // get reattached to the site header.
    let sortNavNodesBy = isSm ? 'data-menu-attach' : 'data-header-attach'

    let sortedNavNodes = navNodes
      .filter(
        // If the nav menu is getting destroyed, filter out the copied nodes
        // that won't need to get reattached before sorting:
        node => isSm || node.getAttribute(sortNavNodesBy)
      )
      .sort(
        // Tab order must match visual order!
        // Sort by value of [data-menu-attach] or [data-header-attach],
        // so we don't have to resort to any questionable nonsense with
        // the tabindex attribute.
        (a, b) => parseInt(a.getAttribute(sortNavNodesBy)) - parseInt(b.getAttribute(sortNavNodesBy))
      )

    // Put the sorted sections in the nav fragment:
    sortedNavNodes.forEach(node => {
      // When creating the nav menu, copy these nodes from the DOM
      // instead of detaching them.
      let shouldMakeCopy = isSm && node.dataset.menuCopy === 'true'

      // When creating the nav menu, detach these nodes entirely
      // and store for reattaching.
      let shouldDetachNode = isSm && (node.dataset.menuDetach === '' || node.dataset.menuDetach === 'true')

      if (shouldMakeCopy) {
        logger('copying node:', node)
        let nodeClone = node.cloneNode(true)
        navCollection.appendChild(nodeClone)
        return
      }

      if (shouldDetachNode) {
        logger('detaching node:', node)
        let nodeClone = node.cloneNode(true)
        this.detachedNodes.push(nodeClone)
        node.remove()
        return
      }

      // Otherwise, detach and reattach the node in the site's header,
      // or restore the node there if it was stashed in this.detachedNodes.
      if (isSm) {
        logger('reattaching node to nav menu:', node)
      } else {
        logger('restoring node to site header:', node)
      }
      navCollection.appendChild(node)
    })

    // When destroying the nav menu, this is the time to also empty
    // this.detachedNodes:
    if (!isSm) {
      this.detachedNodes = []
    }

    logger('nav sections in order:', navCollection)

    return navCollection
  }

  setMenuTopOffset() {
    // Calculates header's current height and stores it in a custom property
    let headerDimensions = this.siteHeader.getBoundingClientRect()
    logger('main nav height:', parseFloat(headerDimensions.height).toFixed(2))
    document.documentElement.style.setProperty('--main-nav-height', `${parseFloat(headerDimensions.height).toFixed(2)}px`)
  }

  handleButtonContent(isMenuShown) {
    this.navMenuBtn.setAttribute('aria-expanded', isMenuShown)
    this.navMenuBtn.innerHTML = isMenuShown ? this.#activeButtonContents : this.#staticButtonContents
  }

  handleButtonToggle(event) {
    if (this.menuDialog.shown) {
      this.close(event)
    } else {
      this.open(event)
    }
  }

  handleBreakpoint(event) {
    if (event.matches) {
      this.setupMenu()
    } else {
      this.destroyMenu()
    }
  }

  handleAnchorLink(event) {
    let currentUrl = new URL(document.URL)
    let currentPath = currentUrl.pathname
    let targetPath = event.target.pathname
    let targetHasHash = event.target.hash

    if (currentPath === targetPath && targetHasHash) {
      // Target is an anchor link on the current page,
      // so close the menu dialog because it won't be
      // dismissed automatically with a page reload
      this.close(event)
    }
  }

  handleResize(event) {
    this.setMenuTopOffset()
  }
}
